/**
 * Générateur — Automatisme : donner la probabilité sous diverses formes
 * (fraction, décimale, pourcentage) pour les cinq évènements : impossible ;
 * certain ; pile (pièce équilibrée) ; une valeur d'un dé équilibré ; une
 * couleur de boule lors d'un tirage dans une urne.
 * Attendu ID : 1082 | Niveau : 5ème | Domaine : Probabilités
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Quelle probabilité ?               (QCM, forme imposée)
 *   - Chaque évènement à sa probabilité  (relier, formes mélangées + leurre)
 *   - La chaîne des trois écritures      (texte à trous drag)
 *   - Trois écritures, un même nombre    (V/F, cadence raisonnement)
 *
 * ── Plan de cartes validé (2026-09-01) ─────────────────────────────────────
 * Source : Automatisme n°44 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * APPUI 6e MAJEUR (P9) : « a chances sur b → quotient a/b, exprimable en
 * fraction, nombre décimal ou pourcentage » — les équivalences 1/2 = 0,5 =
 * 50 % sont disponibles en un geste. Champ (P3) : dénominateurs 2, 4, 5, 6,
 * 10 ; décimaux à 2 décimales max ; pourcentages entiers.
 * OBSTACLES P11 (documentés par la fiche) :
 *   - 1/6 N'EST PAS un nombre décimal (0,17 n'est qu'approché, question b.) —
 *     le dé n'est JAMAIS interrogé en écriture décimale ni en pourcentage,
 *     seulement en fraction (garde-fou par construction, throw sinon) ;
 *   - une probabilité ne dépasse jamais 1 ni 100 % (question d., le 150 %) ;
 *   - confusions d'écriture : complément (75 % pour 25 %), inversion (4/1),
 *     décalage de virgule (2,5), numérateur seul (1 %) — pièges SERVIS en
 *     distracteurs, jamais corrects (recomptés numériquement, throw).
 * INVARIANTS :
 *   - toutes les équivalences sont RECOMPTÉES (fraction → décimal → %,
 *     valeurs entières/exactes exigées, throw si écart) ; fractions d'urne
 *     irréductibles par construction ;
 *   - texte à trous : les deux trous sont VERROUILLÉS par leur étiquette
 *     (« En écriture décimale : … », « En pourcentage : … ») — l'échange
 *     des deux réponses est grammaticalement faux, pas de paire commutative ;
 *   - relier : probabilités deux à deux distinctes (throw), une cible leurre
 *     codée (complément), annoncée dans la consigne ;
 *   - vocabulaire d'élève partout (P7/P8), pas de calculatrice.
 */
import {
    randomChoice, shuffle,
    makeQcm, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1082;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QCM = 'Quelle probabilité ?';
const L_RELIER = 'Chaque évènement à sa probabilité';
const L_TROU = 'La chaîne des trois écritures';
const L_VF = 'Trois écritures, un même nombre';

// ─── Écritures : rendus exacts et vérifiés ─────────────────────────────────

const gcd = (a, b) => (b ? gcd(b, a % b) : a);

/** Garde-fous : n/d est une probabilité, irréductible hors 0 et 1. */
const verifierFraction = (n, d) => {
    if (!(d > 0) || n < 0 || n > d) throw new Error('gen_1082 : fraction hors [0 ; 1]');
    if (n > 0 && n < d && gcd(n, d) !== 1) throw new Error('gen_1082 : fraction réductible dans le vivier');
};

const fmtFrac = (n, d) => `${n}/${d}`;

/** Écriture décimale EXACTE (≤ 2 décimales), throw sinon. */
const fmtDec = (n, d) => {
    if ((100 * n) % d !== 0) throw new Error('gen_1082 : écriture décimale inexacte demandée');
    const v = n / d;
    return String(v).replace('.', ',');
};

/** Pourcentage ENTIER, throw sinon. */
const fmtPct = (n, d) => {
    if ((100 * n) % d !== 0) throw new Error('gen_1082 : pourcentage non entier demandé');
    return `${(100 * n) / d} %`;
};

/** Valeur numérique d'une écriture affichée (pour les recomptages). */
const valeurDe = (s) => {
    const f = String(s).match(/^(\d+)\/(\d+)$/);
    if (f) return +f[1] / +f[2];
    const p = String(s).match(/^(\d+(?:,\d+)?) %$/);
    if (p) return parseFloat(p[1].replace(',', '.')) / 100;
    return parseFloat(String(s).replace(',', '.'));
};

// ─── Le domaine : les cinq évènements de l'intitulé ────────────────────────
// Urnes [rouges, vertes] : chaque couleur donne une fraction IRRÉDUCTIBLE
// de dénominateur 4, 5 ou 10 (équivalences exactes garanties).

// Compositions [majoritaire, minoritaire] : fractions irréductibles, formes
// exactes (dénominateurs 4, 5, 10, 20 — décimaux à 2 décimales max, P3).
const URNES_1082 = [[3, 1], [4, 1], [3, 2], [7, 3], [9, 1], [17, 3], [13, 7], [11, 9]];
const VALEURS_DE = [2, 3, 4, 5];
// Supports « couleur » (revue 2026-09-01, décision prof) : urne de boules,
// sac de jetons, roue à secteurs égaux — même objet mathématique, situations
// simples d'équiprobabilité (appui 6e). Jeu de cartes écarté (composition du
// paquet = connaissance extra-mathématique, dénominateurs hors champ).
// Palettes [majoritaire, minoritaire, ABSENTE], accordées au genre du support.
const PALETTES_FEM = [['rouge', 'verte', 'bleue'], ['bleue', 'jaune', 'rouge']];
const PALETTES_MASC = [['rouge', 'vert', 'bleu'], ['bleu', 'jaune', 'rouge']];

const libSupport = (sup, r, v, c1, c2) => {
    const p2 = v > 1 ? 's' : '';
    if (sup === 'urne') return `dans une urne qui contient ${r} boules ${c1}s et ${v} boule${p2} ${c2}${p2}`;
    if (sup === 'sac') return `dans un sac qui contient ${r} jetons ${c1}s et ${v} jeton${p2} ${c2}${p2}`;
    return `partagée en ${r + v} secteurs égaux : ${r} ${c1}s et ${v} ${c2}${p2}`;
};

const tirerEvenement1082 = (type) => {
    const [r, v] = randomChoice(URNES_1082);
    const sup = randomChoice(['urne', 'sac', 'roue']);
    const [c1, c2, absente] = randomChoice(sup === 'urne' ? PALETTES_FEM : PALETTES_MASC);
    const d = r + v;
    const evtCouleur = (c) => (sup === 'urne'
        ? `Tirer une boule ${c} ${libSupport('urne', r, v, c1, c2)}`
        : sup === 'sac'
            ? `Tirer un jeton ${c} ${libSupport('sac', r, v, c1, c2)}`
            : `Obtenir un secteur ${c} avec une roue équilibrée ${libSupport('roue', r, v, c1, c2)}`);
    if (type === 'impossible') {
        return randomChoice([
            { t: evtCouleur(absente), n: 0, d },
            { t: 'Obtenir 7 en lançant un dé équilibré à six faces', n: 0, d: 6 },
        ]);
    }
    if (type === 'certain') {
        return randomChoice([
            { t: evtCouleur(`${c1} ou ${c2}`), n: d, d },
            { t: 'Obtenir un nombre plus petit que 7 en lançant un dé équilibré à six faces', n: 6, d: 6 },
        ]);
    }
    if (type === 'pile') {
        const face = randomChoice(['pile', 'face']);
        return { t: `Obtenir ${face} en lançant une pièce équilibrée`, n: 1, d: 2 };
    }
    if (type === 'de') {
        return { t: `Obtenir ${randomChoice(VALEURS_DE)} en lançant un dé équilibré à six faces`, n: 1, d: 6 };
    }
    // couleur : une cible parmi les deux, fraction irréductible par construction
    const couleur = randomChoice([c1, c2]);
    return { t: evtCouleur(couleur), n: couleur === c1 ? r : v, d };
};

const TYPES_1082 = ['impossible', 'certain', 'pile', 'de', 'urne'];

/**
 * BRIDE DE FORME (revue 2026-09-01, décision prof) : les équivalences en un
 * geste (P9, champ de la fiche) sont les moitiés, quarts et dixièmes. Les
 * cinquièmes et vingtièmes exigeraient une amplification (4/5 = 8/10 = 0,8),
 * une ÉTAPE de conversion — hors automatisme. Ils restent donc en FRACTION,
 * comme le 1/6 du dé. Exception : 0 et 1 (impossible/certain) gardent les
 * trois formes, aucune conversion n'est en jeu.
 */
const formesLibres = (n, d) => n === 0 || n === d || [2, 4, 10].includes(d);

// ─── Format 1 : QCM à forme imposée ────────────────────────────────────────

// Banques de secours par forme (valeurs canoniques du champ, jamais égales
// à la bonne réponse après filtre numérique).
const BANQUE_FRACTIONS = ['1/2', '1/4', '3/4', '1/6', '1/5', '3/10', '1/10', '2/5'];
const BANQUE_DECIMALES = ['0,5', '0,25', '0,75', '0,2', '0,3', '0,7', '0,1', '0,4'];
const BANQUE_POURCENTS = ['50 %', '25 %', '75 %', '20 %', '30 %', '70 %', '10 %', '40 %'];

// Anti-répétition immédiate (recette standard).
let derniereSignatureQcm1082 = null;

const genEvenementQcm = () => {
    let payload = construireEvenementQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureQcm1082; t++) {
        payload = construireEvenementQcm();
    }
    derniereSignatureQcm1082 = payload.texte;
    return payload;
};

const construireEvenementQcm = () => {
    const type = randomChoice(TYPES_1082);
    const ev = tirerEvenement1082(type);
    verifierFraction(ev.n, ev.d);
    // GARDE-FOUS : le dé (1/6) n'a pas d'écriture décimale exacte, et les
    // cinquièmes/vingtièmes demanderaient une conversion (bride formesLibres).
    const forme = formesLibres(ev.n, ev.d) ? randomChoice(['fraction', 'decimale', 'pourcentage']) : 'fraction';

    const rendre = (f) => (f === 'fraction' ? fmtFrac(ev.n, ev.d) : f === 'decimale' ? fmtDec(ev.n, ev.d) : fmtPct(ev.n, ev.d));
    const correct = rendre(forme);
    const p = ev.n / ev.d;

    // Pièges P11 d'abord (complément, inversion, décalage, numérateur seul),
    // puis complétion par la banque de la forme.
    const candidats = [];
    if (forme === 'fraction') {
        candidats.push(`${ev.d - ev.n}/${ev.d}`);
        if (ev.n > 0 && ev.n < ev.d && ev.n !== ev.d - ev.n) candidats.push(`${ev.d}/${ev.n}`);
        candidats.push(...shuffle([...BANQUE_FRACTIONS]));
    } else if (forme === 'decimale') {
        if ((100 * (ev.d - ev.n)) % ev.d === 0) candidats.push(fmtDec(ev.d - ev.n, ev.d));
        // Décalage de virgule ×10, calculé en ENTIERS (jamais de flottant JS).
        candidats.push(String(((100 * ev.n) / ev.d) / 10).replace('.', ','));
        candidats.push(`${ev.n},${ev.d}`);
        candidats.push(...shuffle([...BANQUE_DECIMALES]));
    } else {
        if ((100 * (ev.d - ev.n)) % ev.d === 0) candidats.push(fmtPct(ev.d - ev.n, ev.d));
        candidats.push(`${ev.n} %`);
        candidats.push(`${String(p).replace('.', ',')} %`);
        candidats.push(...shuffle([...BANQUE_POURCENTS]));
    }
    const distracteurs = [];
    for (const c of candidats) {
        if (distracteurs.length >= 3) break;
        if (c === correct) continue;
        const vc = valeurDe(c);
        if (!Number.isFinite(vc) || Math.abs(vc - p) < 1e-9) continue; // jamais une écriture équivalente
        if (!distracteurs.includes(c)) distracteurs.push(c);
    }
    if (distracteurs.length < 3) throw new Error('gen_1082 : distracteurs insuffisants');

    const nomForme = forme === 'fraction' ? 'sous forme de fraction' : forme === 'decimale' ? 'en écriture décimale' : 'en pourcentage';
    const texte = `<p>« ${ev.t} »</p><p>Quelle est la probabilité de cet évènement, ${nomForme} ?</p>`;
    const payload = makeQcm([correct, ...distracteurs], correct, texte, opts(L_QCM));
    payload._meta.params = { type, forme, n: ev.n, d: ev.d };
    return payload;
};

// ─── Format 2 : relier évènements → probabilités (formes mélangées) ────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureRelier1082 = null;

const genProbaRelier = () => {
    let payload = construireProbaRelier();
    for (let t = 0; t < 8 && payload.texte + payload.contenu.bonne_reponse === derniereSignatureRelier1082; t++) {
        payload = construireProbaRelier();
    }
    derniereSignatureRelier1082 = payload.texte + payload.contenu.bonne_reponse;
    return payload;
};

const construireProbaRelier = () => {
    for (let essai = 0; essai < 40; essai++) {
        const types = shuffle([...TYPES_1082]).slice(0, 4);
        const evts = types.map((ty) => ({ type: ty, ...tirerEvenement1082(ty) }));
        evts.forEach((e) => verifierFraction(e.n, e.d));
        const ps = evts.map((e) => e.n / e.d);
        if (new Set(ps).size !== 4) throw new Error('gen_1082 : deux évènements de même probabilité au relier');

        // Formes mélangées ; dé, cinquièmes et vingtièmes restent en fraction
        // (bride formesLibres) ; au moins 2 formes distinctes.
        const formes = evts.map((e) => (formesLibres(e.n, e.d) ? randomChoice(['fraction', 'decimale', 'pourcentage']) : 'fraction'));
        if (new Set(formes).size < 2) continue;
        const cibles = evts.map((e, i) => (formes[i] === 'fraction' ? fmtFrac(e.n, e.d) : formes[i] === 'decimale' ? fmtDec(e.n, e.d) : fmtPct(e.n, e.d)));
        if (new Set(cibles).size !== 4) continue;

        // Leurre P11 : le complément d'un évènement non trivial, dans sa forme,
        // jamais égal à une probabilité du lot (recompté, sinon on re-tire).
        const idx = evts.findIndex((e) => e.n > 0 && e.n < e.d);
        if (idx < 0) continue;
        const e = evts[idx];
        const leurre = formes[idx] === 'fraction' ? fmtFrac(e.d - e.n, e.d)
            : formes[idx] === 'decimale' ? fmtDec(e.d - e.n, e.d) : fmtPct(e.d - e.n, e.d);
        if (ps.some((v) => Math.abs(v - valeurDe(leurre)) < 1e-9) || cibles.includes(leurre)) continue;

        const pairs = evts.map((e2, i) => ({ source: e2.t, target: cibles[i] }));
        const texte = '<p>Relie chaque évènement à sa probabilité.</p>'
            + '<p>Attention : une des probabilités proposées ne correspond à aucun évènement.</p>';
        const payload = makeRelier(pairs, texte, {
            ...opts(L_RELIER),
            cibles_leurres: [{ target: leurre, code: 'complement' }],
        });
        payload._meta.params = { types, ps, leurre };
        return payload;
    }
    throw new Error('gen_1082 : relier introuvable après 40 essais');
};

// ─── Format 3 : la chaîne des trois écritures (texte à trous) ──────────────
// Trous VERROUILLÉS par leur étiquette (« En écriture décimale », « En
// pourcentage ») : l'échange des deux réponses est faux par la grammaire —
// pas de paire commutative (grille A1). Valeurs exactes uniquement.

// Anti-répétition immédiate (recette standard).
let derniereSignatureTrou1082 = null;

const genChaineTrou = () => {
    let payload = construireChaineTrou();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureTrou1082; t++) {
        payload = construireChaineTrou();
    }
    derniereSignatureTrou1082 = payload.texte;
    return payload;
};

const construireChaineTrou = () => {
    // La chaîne exige les trois formes : uniquement moitiés, quarts, dixièmes
    // (bride formesLibres). La CATÉGORIE est fixée d'abord (revue 2026-09-01,
    // A8 : la boucle de rejet donnait 49 % de « 1/2 = 0,5 = 50 % ») — pièce
    // 1 fois sur 4, sinon support couleur en quarts ou dixièmes.
    let ev;
    if (Math.random() < 0.25) {
        ev = tirerEvenement1082('pile');
    } else {
        let essai = 0;
        do {
            ev = tirerEvenement1082('urne');
            essai++;
        } while (![4, 10].includes(ev.d) && essai < 60);
        if (![4, 10].includes(ev.d)) throw new Error('gen_1082 : composition en quarts/dixièmes introuvable');
    }
    verifierFraction(ev.n, ev.d);
    const frac = fmtFrac(ev.n, ev.d);
    const dec = fmtDec(ev.n, ev.d);
    const pct = fmtPct(ev.n, ev.d);

    const phrase = `La probabilité de cet évènement est ${frac}. En écriture décimale : {{${dec}}}. En pourcentage : {{${pct}}}.`;
    // Distracteurs de drag : complément, décalage, numérateur seul — jamais
    // égaux aux réponses (filtre numérique).
    const p = ev.n / ev.d;
    const candidats = [];
    if ((100 * (ev.d - ev.n)) % ev.d === 0 && ev.d - ev.n !== ev.n) {
        candidats.push(fmtDec(ev.d - ev.n, ev.d), fmtPct(ev.d - ev.n, ev.d));
    }
    candidats.push(String(((100 * ev.n) / ev.d) / 10).replace('.', ','), `${ev.n} %`, `${ev.n},${ev.d}`);
    const distracteurs = [];
    for (const c of candidats) {
        if (distracteurs.length >= 4) break;
        if (c === dec || c === pct || distracteurs.includes(c)) continue;
        const vc = valeurDe(c);
        if (Number.isFinite(vc) && Math.abs(vc - p) < 1e-9) continue;
        distracteurs.push(c);
    }
    if (distracteurs.length < 3) throw new Error('gen_1082 : distracteurs de drag insuffisants');

    const texte = `<p>« ${ev.t} »</p><p>Complète la chaîne des trois écritures.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_TROU), distracteurs });
    payload._meta.params = { n: ev.n, d: ev.d };
    return payload;
};

// ─── Format 4 : V/F logique (trois écritures, un même nombre) ──────────────

const POOL_ECRITURES = [
    { l: '50 % et 1/2 désignent le même nombre.', v: true },
    { l: 'Un pourcentage est une fraction de dénominateur 100.', v: true },
    { l: '3/4 = 0,75 = 75 %.', v: true },
    { l: '0,3 = 30 %.', v: true },
    // P11 (fiche, question b.) : 1/6 n'est PAS un nombre décimal.
    { l: 'La probabilité 1/6 s\'écrit exactement 0,17.', v: false },
    { l: 'Une probabilité peut valoir 150 %.', v: false },
    { l: '1/4 = 1,4.', v: false },
    { l: '2 % = 0,2.', v: false },
    // Élargissement (revue 2026-09-01) : pool porté à 6 V / 6 F pour interdire
    // STRICTEMENT toute reprise d'une ligne au tirage suivant.
    { l: '1/10 = 0,1 = 10 %.', v: true },
    { l: '0 % et 0 désignent la même probabilité.', v: true },
    { l: '0,75 = 7,5 %.', v: false },
    { l: 'La fraction 1/2 s\'écrit aussi 12 %.', v: false },
];

// INTERDICTION STRICTE de reprise (revue 2026-09-01, décision prof) : aucune
// ligne du tirage précédent ne ressort à l'actualisation suivante — pool de
// 12 (6 V / 6 F), la mixité reste toujours atteignable après exclusion.
let derniersServisVf1082 = [];

const genEcrituresVf = () => {
    let tirage = null;
    for (let essai = 0; essai < 60 && !tirage; essai++) {
        const dispo = POOL_ECRITURES.filter((it) => !derniersServisVf1082.includes(it.l));
        const cand = shuffle(dispo).slice(0, 4);
        const nbFaux = cand.filter((it) => !it.v).length;
        if (nbFaux >= 1 && nbFaux <= 3) tirage = cand;
    }
    if (!tirage) throw new Error('gen_1082 : tirage V/F écritures sans mixité après 60 essais');
    derniersServisVf1082 = tirage.map((it) => it.l);

    const payload = makeVraiFaux(
        tirage.map((it) => it.l),
        tirage.map((it) => (it.v ? 0 : 1)),
        '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
        opts(L_VF),
    );
    // Métadonnée DORMANTE (convention gen_1039) : cadence de raisonnement.
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    return payload;
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    evenement_qcm_unique: genEvenementQcm,
    proba_relier: genProbaRelier,
    chaine_texte_trou: genChaineTrou,
    ecritures_tableau_vf: genEcrituresVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier', texte_trou: 'Texte à trous', tableau_vf: 'Vrai/Faux',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'evenement', label: L_QCM, formatsList: buildFormats('evenement') },
    { id: 'proba', label: L_RELIER, formatsList: buildFormats('proba') },
    { id: 'chaine', label: L_TROU, formatsList: buildFormats('chaine') },
    { id: 'ecritures', label: L_VF, formatsList: buildFormats('ecritures') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
